import { ConventionLocationStyle } from "app-constants/constants"
import { createPath, createURL_SSR, ROUTE } from "app-constants/routing"
import { ActivityIndicator } from "components/activity-indicator"
import MainLayout from "components/main-layout"
import { SessionsNavV2 } from "components/sessions/SessionsNavV2"
import { useSessionsFilter } from "hooks/sessions/useSessionsFilter"
import { useUser } from "hooks/useUser"
import { api } from "lib/api"
import buildServerSidePage from "lib/build-server-side-page"
import buildServerSideProps from "lib/build-server-side-props"
import deleteConventionSessionRegistration from "lib/deleteConventionSessionRegistration"
import applyCache from "lib/mw/apply-cache"
import applyConvention from "lib/mw/apply-convention"
import applyCurrentServerContext from "lib/mw/apply-current-server-context"
import applyNextSeo from "lib/mw/apply-next-seo"
import applyRedirectForAutomaticallyGeneratedConvention from "lib/mw/apply-redirect-for-automatically-generated-convention"
import applyTitle from "lib/mw/apply-title"
import postActiveAuthenticatedVisitors from "lib/postActiveAuthenticatedVisitors"
import useCurrent from "lib/use-current"
import Convention from "models/convention-api/v1/Convention"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import ConventionSessionCount from "models/convention-api/v1/ConventionSessionCount"
import { useRouter } from "next/router"
import React, { useEffect, useState } from "react"
import { useDebounce } from "use-debounce"
import { assertCurrent } from "utils/asserts"

export const getServerSideProps = buildServerSideProps(
  applyCache(),
  applyCurrentServerContext(),
  applyTitle(({ props }) => `${props.current.config.session_copy_text}`),
  applyConvention(),
  applyRedirectForAutomaticallyGeneratedConvention(),
  [
    (context) => {
      if (context.props.convention?.location_style == ConventionLocationStyle.INPERSON) {
        context.props.redirect = createPath({
          path: ROUTE.CONVENTION,
          params: { conventionId: context.params.convention_id as string },
        })
        context.abort()
        return
      }
    },
  ],
  applyNextSeo((context) => ({
    openGraph: {
      title: `${context.props.convention.name} - Live Sessions`,
      url: createURL_SSR(context, {
        path: ROUTE.CONVENTION_SESSIONS,
        params: { conventionId: context.props.convention.id },
      }),
      type: "website",
      images: [
        {
          url: context.props.convention.logo_url,
          alt: `${context.props.convention.name} - Live Sessions`,
        },
      ],
    },
  })),
)

interface ConventionSessionsPageProps {}

export default buildServerSidePage(function ConventionSessionsPage(
  _props: ConventionSessionsPageProps,
) {
  const router = useRouter()
  const conventionId = router.query.convention_id as string

  const [userSessionIds, setUserSessionIds] = useState<Record<string, boolean>>({})
  const current = useCurrent()
  const user = useUser()
  // @ts-ignore
  const providerConfig = current.config

  const sessionsFilter = useSessionsFilter()
  const [sessionsFilterDebounce] = useDebounce(sessionsFilter, 250)

  const { data: convention } = Convention.useLoadOne(conventionId)
  const sessionsCountModel = ConventionSessionCount.useOne({
    filter: sessionsFilterDebounce.filters,
  })

  const sessions = ConventionSession.useMore({
    limit: 10,
    filter: sessionsFilterDebounce.filters,
    includes: ["capacity", "convention_exhibitor"],
  })

  // @ts-ignore
  const userSessionsModel = ConventionSession.useAll(() => {
    if (!user) return null

    return {
      filter: {
        ...sessionsFilterDebounce.filters,
        convention_user_id: user.id,
      },
    }
  })
  const sessionsPages = sessions.data ?? []
  const sessionsFlat = sessionsPages.flat()

  const isSessionsReady =
    sessionsCountModel.data !== undefined &&
    sessions.data !== undefined &&
    // if the user is not logged in, we don't check userSessionsModel is loaded
    ((!user && userSessionsModel.data === undefined) || userSessionsModel.data !== undefined)

  const isLoading =
    sessions.isLoading || sessionsCountModel.isValidating || userSessionsModel.isValidating
  const sessionsCount = sessionsCountModel.data?.session_count ?? 0

  const onSetReminder = async (sessionId: string) => {
    try {
      await api.registerForSession({ req: undefined }, sessionId)
      setUserSessionIds((prevUserSessionIds) => ({ ...prevUserSessionIds, [sessionId]: true }))
    } catch (error) {
      console.error(error)
    }
  }

  const onRemoveReminder = async (sessionId: string) => {
    try {
      assertCurrent(current)

      await deleteConventionSessionRegistration({ current, sessionId })
      setUserSessionIds((prevUserSessionIds) => ({ ...prevUserSessionIds, [sessionId]: false }))
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (!userSessionsModel.data) return

    setUserSessionIds(
      (userSessionsModel.data ?? []).reduce<Record<string, boolean>>((store, currentSession) => {
        store[currentSession.id] = true
        return store
      }, {}),
    )
  }, [userSessionsModel.data])

  useEffect(() => {
    const postActiveUser = async () => {
      try {
        await postActiveAuthenticatedVisitors({
          current,
          // @ts-ignore
          convention_convention_id: convention.id,
        })
      } catch (error) {
        console.error(error)
      }
    }

    // @ts-ignore
    if (current.user && convention) {
      postActiveUser()
    }
  }, [current, convention])

  if (!providerConfig || !router.query.convention_id || !convention) {
    return <ActivityIndicator />
  }

  return (
    <MainLayout layout="micro">
      <SessionsNavV2
        onSetReminder={onSetReminder}
        onRemoveReminder={onRemoveReminder}
        sessionsCount={sessionsCount}
        eventHeaderPageName={providerConfig.nav_session_copy_text ?? "Sessions"}
        eventHeaderPageText={providerConfig.session_copy_text}
        sessions={sessionsFlat}
        userSessionIds={userSessionIds}
        isLoading={isLoading}
        isReady={isSessionsReady}
        loadMore={sessions.loadMore}
        hasMore={sessions.hasMore}
        activeFiltersCount={sessionsFilterDebounce.activeFilters}
      />
    </MainLayout>
  )
})
